import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Parse a date string to a Date object, returning null for empty/invalid values
const parseDate = (val) => {
    if (!val) return null;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
};

// Numeric time input: only digits, max 2 chars, auto-pads on blur, clamped to max
// Has local state so typing works, commits to parent on blur
// Up/down arrows increment/decrement by 1
const TimeInput = ({ value, max, onChange, disabled }) => {
    const [localVal, setLocalVal] = useState(value);

    useEffect(() => {
        setLocalVal(value);
    }, [value]);

    const handleChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
        setLocalVal(raw);
    };

    const commit = (val) => {
        let num = parseInt(val, 10);
        if (isNaN(num)) num = 0;
        if (num > max) num = max;
        if (num < 0) num = 0;
        const padded = num.toString().padStart(2, '0');
        setLocalVal(padded);
        onChange(padded);
    };

    const handleBlur = () => commit(localVal);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') commit(localVal);
        if (e.key === 'ArrowUp') { e.preventDefault(); increment(1); }
        if (e.key === 'ArrowDown') { e.preventDefault(); increment(-1); }
    };

    const increment = (delta) => {
        let num = parseInt(localVal, 10);
        if (isNaN(num)) num = 0;
        num += delta;
        if (num > max) num = 0;
        if (num < 0) num = max;
        const padded = num.toString().padStart(2, '0');
        setLocalVal(padded);
        onChange(padded);
    };

    return (
        <div className="datetime-time-wrapper">
            <input
                type="text"
                className="datetime-time-input"
                value={localVal}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                maxLength={2}
            />
            <div className="datetime-time-arrows">
                <button type="button" className="datetime-arrow" onClick={() => increment(1)} disabled={disabled} tabIndex={-1}>&#9650;</button>
                <button type="button" className="datetime-arrow" onClick={() => increment(-1)} disabled={disabled} tabIndex={-1}>&#9660;</button>
            </div>
        </div>
    );
};

// Date + Time row: calendar picker, HH:MM:SS inputs, and reset button
const DateTimeRow = ({ value, onChange, placeholderText }) => {
    const dateObj = parseDate(value);
    const h = dateObj ? dateObj.getHours().toString().padStart(2, '0') : '00';
    const m = dateObj ? dateObj.getMinutes().toString().padStart(2, '0') : '00';

    const handleDatePick = (date) => {
        if (!date) { onChange(''); return; }
        const existing = parseDate(value);
        if (existing) {
            date.setHours(existing.getHours(), existing.getMinutes(), existing.getSeconds());
        }
        onChange(date.toISOString());
    };

    const updateTime = (hours, mins, secs) => {
        const d = dateObj ? new Date(dateObj) : new Date();
        d.setHours(hours, mins, secs, 0);
        onChange(d.toISOString());
    };

    const handleHourChange = (val) => {
        const num = parseInt(val, 10);
        if (!isNaN(num)) updateTime(Math.min(num, 23), parseInt(m, 10), 0);
    };
    const handleMinuteChange = (val) => {
        const num = parseInt(val, 10);
        if (!isNaN(num)) updateTime(parseInt(h, 10), Math.min(num, 59), 0);
    };

    return (
        <div className="datetime-row">
            <DatePicker
                selected={dateObj}
                onChange={handleDatePick}
                dateFormat="yyyy-MM-dd"
                calendarStartDay={1}
                placeholderText={placeholderText}
                className="datetime-datepicker"
            />
            <TimeInput value={h} max={23} onChange={handleHourChange} disabled={!dateObj} />
            <span className="datetime-separator">:</span>
            <TimeInput value={m} max={59} onChange={handleMinuteChange} disabled={!dateObj} />
            {dateObj && (
                <button
                    type="button"
                    className="datetime-clear"
                    onClick={() => onChange('')}
                    title="Clear"
                >
                    &#x21bb;
                </button>
            )}
        </div>
    );
};

export default DateTimeRow;
